/**
 * Frontend Logger Utility - PCI DSS Compliant
 * 
 * Provides sanitized logging for frontend errors and operations
 * Ensures compliance with banking/payments industry standards
 * Prevents PII and sensitive data from being logged
 * 
 * Usage:
 * import logger from '../utils/logger';
 * logger.error('Operation failed', { error: err.message, requestId: '123' });
 */

class FrontendLogger {
  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.appName = 'LynxPay-Frontend';
  }

  /**
   * Sanitize data to prevent PII and sensitive information logging
   * Removes customer data, payment details, and other sensitive fields
   * 
   * @param {any} data - Data to sanitize
   * @returns {any} Sanitized data safe for logging
   */
  sanitize(data) {
    if (!data) return data;
    
    if (typeof data === 'string') {
      // Don't log if it looks like sensitive data
      if (this.containsSensitiveData(data)) {
        return '[SANITIZED_STRING]';
      }
      return data;
    }
    
    if (typeof data !== 'object') return data;
    
    if (Array.isArray(data)) {
      return data.map(item => this.sanitize(item));
    }
    
    const sanitized = { ...data };
    
    // Remove PII and sensitive payment fields
    const sensitiveFields = [
      'customer_email', 'email', 'customer_phone', 'phone',
      'payment_method_details', 'card_data', 'card_number', 'cvv',
      'bank_account', 'routing_number', 'account_number',
      'ssn', 'tax_id', 'password', 'token', 'secret',
      'billing_address', 'shipping_address'
    ];
    
    sensitiveFields.forEach(field => {
      if (sanitized[field]) {
        sanitized[field] = '[SANITIZED]';
      }
    });
    
    // Recursively sanitize nested objects
    Object.keys(sanitized).forEach(key => {
      if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
        sanitized[key] = this.sanitize(sanitized[key]);
      }
    });
    
    return sanitized;
  }
  
  /**
   * Check if string contains sensitive data patterns
   * @param {string} str - String to check
   * @returns {boolean} True if potentially sensitive
   */
  containsSensitiveData(str) {
    const sensitivePatterns = [
      /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/, // Credit card
      /\b\d{9}\b/, // SSN pattern
      /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/, // Email
      /\b\d{3}-\d{2}-\d{4}\b/, // SSN format
    ];
    
    return sensitivePatterns.some(pattern => pattern.test(str));
  }
  
  /**
   * Format log message with timestamp and context
   * @param {string} level - Log level
   * @param {string} message - Log message
   * @param {object} data - Additional data
   * @returns {object} Formatted log entry
   */
  formatLog(level, message, data = {}) {
    return {
      timestamp: new Date().toISOString(),
      level,
      app: this.appName,
      message,
      data: this.sanitize(data),
      userAgent: navigator?.userAgent ? navigator.userAgent.substring(0, 100) : 'unknown',
      url: window?.location?.pathname || 'unknown'
    };
  }

  /**
   * Log info level messages
   * @param {string} message - Message to log
   * @param {object} data - Additional data
   */
  info(message, data = {}) {
    const logEntry = this.formatLog('INFO', message, data);
    
    if (this.isDevelopment) {
      console.info(`[INFO] ${message}`, logEntry.data);
    }
    
    // In production, send to logging service (e.g., backend /api/logs endpoint)
    this.sendToLoggingService('info', logEntry);
  }

  /**
   * Log warning level messages
   * @param {string} message - Message to log
   * @param {object} data - Additional data
   */
  warn(message, data = {}) {
    const logEntry = this.formatLog('WARN', message, data);
    
    if (this.isDevelopment) {
      console.warn(`[WARN] ${message}`, logEntry.data);
    }
    
    // In production, send to logging service
    this.sendToLoggingService('warn', logEntry);
  }

  /**
   * Log error level messages
   * @param {string} message - Message to log
   * @param {object} data - Additional data
   */
  error(message, data = {}) {
    const logEntry = this.formatLog('ERROR', message, data);
    
    if (this.isDevelopment) {
      console.error(`[ERROR] ${message}`, logEntry.data);
    }
    
    // In production, send to logging service
    this.sendToLoggingService('error', logEntry);
  }

  /**
   * Log debug level messages (development only)
   * @param {string} message - Message to log
   * @param {object} data - Additional data
   */
  debug(message, data = {}) {
    if (this.isDevelopment) {
      const logEntry = this.formatLog('DEBUG', message, data);
      console.debug(`[DEBUG] ${message}`, logEntry.data);
    }
    // Debug logs are not sent to production logging service
  }

  /**
   * Send log entry to backend logging service
   * In development, this is a no-op. In production, sends to backend.
   * 
   * @param {string} level - Log level
   * @param {object} logEntry - Formatted log entry
   */
  sendToLoggingService(level, logEntry) {
    if (this.isDevelopment) {
      return; // Don't send logs to service in development
    }
    
    // In production, send to backend logging endpoint
    try {
      fetch('/api/logs/frontend', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(logEntry)
      }).catch(error => {
        // Fallback: if logging service fails, log to console
        console.error('Failed to send log to service:', error);
      });
    } catch (error) {
      // Silent failure for logging service
      console.error('Logging service error:', error);
    }
  }

  /**
   * Log API errors with standard format
   * @param {string} operation - Operation that failed
   * @param {Error} error - Error object
   * @param {object} context - Additional context
   */
  logApiError(operation, error, context = {}) {
    this.error(`API Error: ${operation}`, {
      operation,
      error: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      requestId: error.response?.data?.requestId,
      ...context
    });
  }

  /**
   * Log performance metrics
   * @param {string} operation - Operation measured
   * @param {number} duration - Duration in milliseconds
   * @param {object} context - Additional context
   */
  logPerformance(operation, duration, context = {}) {
    this.info(`Performance: ${operation}`, {
      operation,
      duration: `${duration}ms`,
      ...context
    });
  }
}

// Export singleton instance
const logger = new FrontendLogger();
export default logger;